import React, { useState } from 'react';
import { motion } from 'framer-motion';

const FuturePacing = () => {
  const [form, setForm] = useState({ name: '', email: '' });
  const [submitted, setSubmitted] = useState(false);
  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = e => { e.preventDefault(); setSubmitted(true); };

  return (
    <section id="anmeldung-formular" className="relative py-36 overflow-hidden bg-dark border-t border-gold/20">
      <motion.div
        animate={{ opacity: [0.4, 0.8, 0.4], scale: [1, 1.08, 1] }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(212,175,55,0.1) 0%, transparent 60%)' }}
      />
      <motion.div
        animate={{ opacity: [0.2, 0.5, 0.2] }}
        transition={{ duration: 14, repeat: Infinity }}
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 80% 80%, rgba(212,175,55,0.06) 0%, transparent 50%)' }}
      />
      {[...Array(25)].map((_, i) => (
        <motion.div key={i} className="absolute rounded-full bg-gold pointer-events-none"
          style={{ width: Math.random() * 3 + 0.5, height: Math.random() * 3 + 0.5, left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: 0.3 }}
          animate={{ y: [-30, 30, -30], opacity: [0.05, 0.5, 0.05] }}
          transition={{ duration: 4 + Math.random() * 5, repeat: Infinity, delay: Math.random() * 5 }}
        />
      ))}

      <div className="relative z-10 max-w-4xl mx-auto px-6">
        <div className="text-center mb-20">
          <motion.p
            initial={{ opacity: 0, letterSpacing: '0.1em' }}
            whileInView={{ opacity: 1, letterSpacing: '0.45em' }}
            viewport={{ once: true }}
            transition={{ duration: 1.5 }}
            className="text-gold uppercase text-xs font-semibold mb-10"
            style={{ textShadow: '0 0 20px rgba(212,175,55,0.6)' }}
          >
            Future Pacing · Deine neue Realität
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="text-5xl md:text-7xl font-serif text-cream leading-tight mb-10"
          >
            Stell dir vor,{' '}
            <span className="italic text-transparent bg-clip-text bg-gold-gradient"
              style={{ filter: 'drop-shadow(0 0 20px rgba(212,175,55,0.4))' }}>
              du wachst morgen auf
            </span>
            {' '}und alles ist anders.
          </motion.h2>

          <div className="space-y-8 text-lg text-cream/70 font-light leading-relaxed mb-14 max-w-3xl mx-auto">
            {[
              { delay: 0.2, text: 'Nicht weil er sich geändert hat. Nicht weil der Markt sich verändert hat. Sondern weil du dich verändert hast. Du spürst zum ersten Mal seit langer Zeit eine tiefe, unerschütterliche Ruhe in deiner Brust. Keine Angst vor dem nächsten Tag. Keine Erschöpfung, die bereits beim Aufwachen da ist. Nur Stille. Klarheit. Und das wissende Lächeln einer Frau, die weiß, wer sie ist.' },
              { delay: 0.4, text: 'Du scrollst nicht mehr nervös durch sein Profil. Du überprüfst nicht mehr deine Business-Zahlen mit zitternden Fingern. Du existierst in einem Zustand des tiefen Vertrauens – in dich, in das Leben, in den Fluss. Und das Merkwürdige? Je mehr du loslässt, desto mehr kommt zu dir. Die richtigen Menschen. Die richtigen Chancen. Die Liebe, die sich nicht wie ein Kampf anfühlt.' },
              { delay: 0.6, highlight: true, text: 'Das ist kein Traum. Das ist dein nächstes Kapitel. Und es beginnt mit einem einzigen Schritt: deiner Anmeldung.' },
            ].map((block, i) => (
              <motion.p key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: block.delay, duration: 0.8 }}
                className={block.highlight ? 'text-cream font-serif italic text-xl' : ''}
              >
                {block.text}
              </motion.p>
            ))}
          </div>
        </div>

        {/* Registration Form */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="relative p-10 md:p-16 overflow-hidden"
          style={{ background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.25)', borderRadius: '2px', boxShadow: '0 0 80px rgba(212,175,55,0.08)' }}
        >
          <div className="absolute top-0 left-0 right-0 h-[1px]"
            style={{ background: 'linear-gradient(to right, transparent, #D4AF37, transparent)' }} />
          <motion.div
            animate={{ opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.05) 0%, transparent 60%)' }}
          />

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center gap-6 py-12 text-center"
            >
              <div className="w-20 h-20 rounded-full border-2 border-gold flex items-center justify-center text-gold text-3xl"
                style={{ boxShadow: '0 0 40px rgba(212,175,55,0.4)' }}>✓</div>
              <h3 className="text-3xl font-serif text-cream">Dein Platz ist reserviert.</h3>
              <p className="text-cream/60 font-light max-w-md leading-relaxed">
                Schau in dein Postfach. Jennifer schickt dir alles, was du brauchst,
                um dich auf deine Transformation vorzubereiten.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5 max-w-lg mx-auto">
              <p className="text-gold tracking-[0.2em] uppercase text-xs mb-8 text-center font-semibold">
                ✦ Kostenfreie Anmeldung · Begrenzte Plätze
              </p>
              <input type="text" name="name" value={form.name} onChange={handleChange}
                placeholder="Dein Vorname" required
                className="w-full bg-white/5 border border-white/10 text-cream placeholder:text-cream/30 px-6 py-4 focus:outline-none focus:border-gold/60 transition-colors duration-500 font-light text-base rounded-sm" />
              <input type="email" name="email" value={form.email} onChange={handleChange}
                placeholder="Deine beste E-Mail-Adresse" required
                className="w-full bg-white/5 border border-white/10 text-cream placeholder:text-cream/30 px-6 py-4 focus:outline-none focus:border-gold/60 transition-colors duration-500 font-light text-base rounded-sm" />
              <motion.button type="submit"
                whileHover={{ scale: 1.04, boxShadow: '0 0 70px rgba(212,175,55,0.8), 0 0 140px rgba(212,175,55,0.4)' }}
                whileTap={{ scale: 0.97 }}
                className="w-full px-10 py-5 bg-gold-gradient text-dark font-bold uppercase tracking-widest text-sm relative overflow-hidden group"
                style={{ boxShadow: '0 0 35px rgba(212,175,55,0.5)', borderRadius: '2px' }}
              >
                <span className="relative z-10">✦ Jetzt meinen Platz sichern →</span>
                <div className="absolute inset-0 bg-white/15 translate-x-[-110%] skew-x-[-20deg] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out" />
              </motion.button>
              <p className="text-xs text-cream/30 tracking-widest text-center">
                Kostenlos · Kein Spam · Jederzeit abmeldbar
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default FuturePacing;